import { getCookieValue } from '@/utils/get-cookie-value';
import { setCookieValue } from '@/utils/set-cookie-value';

const COOKIE_NAME = '__extern_vid';
// 2 years — long-lived identity, not the attribution click-id TTL. Matches
// the lifetime of typical first-party analytics identity cookies (e.g. _ga).
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

/** In-session id when the cookie can't be read or written (private mode, disabled cookies). */
let memoryFallbackId: string | undefined;

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Own first-party visitor id — generated and read without Segment, PostHog or
 * GTM present. Those vendors are meant to inherit this value (e.g. Segment's
 * `analytics.setAnonymousId()`) rather than being its source, so a visitor
 * who rejects consent (and never loads those vendors) keeps the same id as
 * one who accepts. Consumed by `getAnonymousCorrelationId`.
 *
 * Cookie-backed and scoped to `.extern.com` (see `setCookieValue`) so the
 * marketing site and the app share one id. Falls back to an in-memory id for
 * the current session when cookies are blocked; never throws.
 */
export function getFirstPartyVisitorId(): string {
  if (typeof window === 'undefined') {
    if (!memoryFallbackId) {
      memoryFallbackId = generateId();
    }

    return memoryFallbackId;
  }

  try {
    const existing = getCookieValue(COOKIE_NAME);
    if (existing) {
      return existing;
    }
  } catch {
    // document.cookie can throw in some restricted/sandboxed contexts
  }

  const id = memoryFallbackId ?? generateId();

  try {
    setCookieValue(COOKIE_NAME, id, COOKIE_MAX_AGE_SECONDS);
    if (getCookieValue(COOKIE_NAME) !== id) {
      // Write was silently dropped (e.g. cookies disabled) — keep this id
      // in-memory for session stability instead of regenerating it later.
      memoryFallbackId = id;
    }
  } catch {
    memoryFallbackId = id;
  }

  return id;
}
